import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';

import { useCurrentUser } from '@/hooks/useCurrentUser';

export interface NoteStats {
  likes: number;
  reposts: number;
  replies: number;
  likedByMe: boolean;
  repostedByMe: boolean;
}

const EMPTY_STATS: NoteStats = {
  likes: 0,
  reposts: 0,
  replies: 0,
  likedByMe: false,
  repostedByMe: false,
};

/** Engagement stats for a single note: reactions (kind 7), reposts (kind 6), replies (kind 1). */
export function useNoteStats(eventId: string) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery<NoteStats>({
    queryKey: ['note-stats', eventId, user?.pubkey ?? 'anon'],
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [{ kinds: [1, 6, 7], '#e': [eventId], limit: 250 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(6000)]) },
      );

      const likers = new Set<string>();
      const reposters = new Set<string>();
      let replies = 0;

      for (const event of events) {
        // The "target" of a reaction/repost is its last `e` tag (NIP-25).
        const eTags = event.tags.filter(([name]) => name === 'e');
        const targetId = eTags.at(-1)?.[1];

        if (event.kind === 7 && targetId === eventId && event.content !== '-') {
          likers.add(event.pubkey);
        } else if (event.kind === 6 && targetId === eventId) {
          reposters.add(event.pubkey);
        } else if (event.kind === 1 && eTags.some(([, id]) => id === eventId)) {
          replies++;
        }
      }

      return {
        likes: likers.size,
        reposts: reposters.size,
        replies,
        likedByMe: user ? likers.has(user.pubkey) : false,
        repostedByMe: user ? reposters.has(user.pubkey) : false,
      };
    },
    placeholderData: EMPTY_STATS,
  });
}
